import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Upload,
  List,
  Card,
  Button,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Menu,
} from "antd";
import {
  SendOutlined,
  FileOutlined,
  CloseOutlined,
  InboxOutlined,
  DownloadOutlined,
  CloseCircleOutlined,
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  sendMessage,
  getGroupMessages,
  getUserMessages,
  updateMessage,
  deleteMessage,
  replyToMessage,
} from "../../store/messageSlice";
import { fetchUserGroupsByUserId } from "../../store/groupSlice";
import EmojiPickerComponent from "./EmojiPickerComponent";
import AudioRecorderComponent from "./AudioRecorder";
import FileAttachmentComponent from "./FileAttachment";
const { Text } = Typography;

export default function ChatContent({ selectedChat }) {
  // ── REPLY MODE ─────────────────────────────────────────────────────────────
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const messagesEndRef = useRef(null);
  // State variables
  const [editAttachment, setEditAttachment] = useState(null);

  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState("");

  const storedUser = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const senderId = storedUser?.userId;
  // safely pull out type/id fields
  const chatType = selectedChat?.type;
  const chatUserId = selectedChat?.id;
  const chatGroupId = selectedChat?.userGroupID;

  // use AntD’s Dragger for both file & audio (it’ll accept any blob)
  const { Dragger } = Upload;

  <Dragger
    accept="*/*"
    showUploadList={false}
    beforeUpload={(file) => {
      // keep in state instead of uploading automatically
      setPendingAttachments((p) => [...p, file]);
      return false; // prevent auto upload
    }}
    style={{ marginBottom: 16 }}
  >
    <p className="ant-upload-drag-icon">
      <InboxOutlined />
    </p>
    <p className="ant-upload-text">
      Drag files / audio here or click to select
    </p>
  </Dragger>;

  // render previews:
  {
    pendingAttachments.length > 0 && (
      <List
        grid={{ gutter: 8, column: 4 }}
        dataSource={pendingAttachments}
        renderItem={(file, idx) => (
          <List.Item>
            <Card
              size="small"
              cover={
                file.type.startsWith("audio") ? (
                  <audio
                    controlssrc={
                      file instanceof Blob ? URL.createObjectURL(file) : file
                    }
                  />
                ) : file.type.startsWith("image") ? (
                  <img
                    src={
                      file instanceof Blob
                        ? URL.createObjectURL(file)
                        : file /* a data URL fallback */
                    }
                  />
                ) : (
                  <div style={{ padding: 16, textAlign: "center" }}>
                    <FileOutlined style={{ fontSize: 32 }} />
                  </div>
                )
              }
              actions={[
                <Tooltip title="Remove">
                  <CloseCircleOutlined
                    onClick={() =>
                      setPendingAttachments((p) =>
                        p.filter((_, i) => i !== idx)
                      )
                    }
                  />
                </Tooltip>,
              ]}
            >
              <Card.Meta
                title={<span className="text-truncate">{file.name}</span>}
              />
            </Card>
          </List.Item>
        )}
        style={{ marginBottom: 16 }}
      />
    );
  }

  // now split into two buckets
  const { groupMessages = [], privateMessages = [] } = useSelector(
    (state) => state.message
  );

  // pick the right bucket
  const chatMessages =
    chatType === "private"
      ? privateMessages.filter((msg) => {
          const me = senderId;
          const them = chatUserId;
          return (
            (msg.senderId === me && msg.receiverId === them) ||
            (msg.senderId === them && msg.receiverId === me)
          );
        })
      : groupMessages;
  // ── REPLY TARGET ────────────────────────────────────────────────────────
  // the message object we're replying to, or null if none
  const replyTarget = replyToId
    ? chatMessages.find((m) => m.messageId === replyToId)
    : null;
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  // load your full 1:1 inbox once
  useEffect(() => {
    if (senderId) {
      dispatch(getUserMessages(senderId));
    }
  }, [dispatch, senderId]);

  // when they switch groups, still fetch group messages
  useEffect(() => {
    if (chatType === "group" && chatGroupId) {
      dispatch(getGroupMessages(chatGroupId));
    }
  }, [dispatch, chatType, chatGroupId]);

  const handleEditClick = (msg) => {
    setEditingMessageId(msg.messageId);
    setEditText(msg.content);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() && !editAttachment) return;

    const original = chatMessages.find((m) => m.messageId === editingMessageId);
    if (!original) return;

    const updatedData = {
      content: editText.trim() || original.content,
      isReplyAllowed: original.isReplyAllowed,
      isEmoj: original.isEmoj,
      isPinned: original.isPinned,
    };

    if (editAttachment) {
      const base64 = await convertToBase64(editAttachment);
      updatedData.contentAttachment = base64;
      updatedData.attachmentType = editAttachment.type;
    } else if (original.contentAttachment) {
      updatedData.contentAttachment = original.contentAttachment;
      updatedData.attachmentType = original.attachmentType;
    }

    const res = await dispatch(
      updateMessage({ id: editingMessageId, updatedData })
    );

    if (updateMessage.fulfilled.match(res)) {
      setEditingMessageId(null);
      setEditText("");
      setEditAttachment(null);
      if (chatType === "private") dispatch(getUserMessages(senderId));
      else dispatch(getGroupMessages(chatGroupId));
    }
  };

  const handleDeleteClick = async (messageId) => {
    const res = await dispatch(deleteMessage(messageId));
    if (deleteMessage.fulfilled.match(res)) {
      if (chatType === "private") dispatch(getUserMessages(senderId));
      else dispatch(getGroupMessages(chatGroupId));
    }
  };

  const handleSend = async () => {
    if (!input.trim() && pendingAttachments.length === 0) return;

    // 1) send text
    if (input.trim()) {
      await dispatch(
        sendMessage({
          senderId,
          // if private chat, send to user; if group, null
          receiverId: chatType === "private" ? chatUserId : null,
          // if group chat, send to group; if private, null
          groupId: chatType === "group" ? chatGroupId : null,
          content: input.trim(),
          contentAttachment: null,
          attachmentType: null,
          isReplyAllowed: true,
          isEmoj: false,
          replied_To_Id: replyToId,
        })
      );
    }

    // 2) send attachments one by one
    for (let { file, type } of pendingAttachments) {
      const base64 = await convertToBase64(file);
      await dispatch(
        sendMessage({
          senderId,
          receiverId: chatType === "private" ? chatUserId : null,
          groupId: chatType === "group" ? chatGroupId : null,
          content: type === "audio" ? "Voice Message" : file.name,
          contentAttachment: base64,
          attachmentType: file.type,
          isReplyAllowed: true,
          isEmoj: false,
          replied_To_Id: replyToId,
        })
      );
    }

    // 3) clear & refresh
    setInput("");
    setPendingAttachments([]);
    setReplyToId(null); // ◀── clear reply mode
    setReplyText("");
    if (chatType === "private") {
      await dispatch(getUserMessages(senderId));
    } else {
      await dispatch(getGroupMessages(chatGroupId));
    }
  };

  const handleFileSend = async (file) => {
    if (!senderId) return;

    const base64File = await convertToBase64(file);
    if (!base64File) return;

    const payload = {
      senderId,
      receiverId: selectedChat?.receiverId || null,
      groupId: selectedChat?.userGroupID,
      content: file.name || "File Attachment",
      contentAttachment: base64File,
      attachmentType: file.type,
      isReplyAllowed: true,
      isEmoj: false,
      replied_To_Id: replyToId,
    };

    dispatch(sendMessage(payload));
    // refresh the right thread
    if (chatType === "private") dispatch(getUserMessages(senderId));
    else dispatch(getGroupMessages(chatGroupId));
  };

  const handleAudioSend = async (file) => {
    if (!senderId) return;

    const base64Audio = await convertToBase64(file);
    if (!base64Audio) return;

    const payload = {
      senderId,
      receiverId: selectedChat?.receiverId || null,
      groupId: selectedChat?.userGroupID,
      content: "Audio Message",
      contentAttachment: base64Audio,
      attachmentType: "audio/webm",
      isReplyAllowed: true,
      isEmoj: false,
      replied_To_Id: replyToId,
    };

    dispatch(sendMessage(payload));
    // refresh the right thread
    if (chatType === "private") dispatch(getUserMessages(senderId));
    else dispatch(getGroupMessages(chatGroupId));
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ── REPLY HANDLER ─────────────────────────────────────────────────────────
  const handleReplyClick = (message) => {
    setReplyToId(message.messageId);
    setReplyText(`@${message.senderName} `);
  };

  // ── EDIT TARGET ──────────────────────────────────────────────────────────
  // message object we’re editing, or null
  const editTarget = editingMessageId
    ? chatMessages.find((m) => m.messageId === editingMessageId)
    : null;

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    const today = new Date();
    const yesterday = new Date(Date.now() - 86400000);

    // Format the date as "Month Day"
    const options = { month: "long", day: "numeric" };
    const formattedDate = date.toLocaleDateString("en-US", options);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return formattedDate; // Return formatted date like "May 5"
  };

  const getGroupedMessages = () => {
    const grouped = {};
    // Sort messages by the sentAt timestamp
    const sortedMessages = [...chatMessages]
      .filter((msg) => {
        if (selectedChat.type === "group") {
          return msg.groupId === selectedChat.userGroupID;
        } else {
          // for private: show only 1:1 between current user and selected user
          const theirId = selectedChat.id;
          const me = senderId;
          return (
            (msg.senderId === me && msg.receiverId === theirId) ||
            (msg.senderId === theirId && msg.receiverId === me)
          );
        }
      })
      .sort(
        (a, b) =>
          new Date(a.sentAt || a.timestamp || a.createdAt) -
          new Date(b.sentAt || b.timestamp || b.createdAt)
      );

    // Group the messages by their formatted date
    for (const msg of sortedMessages) {
      const ts =
        msg.sentAt ||
        msg.timestamp ||
        msg.createdAt ||
        new Date().toISOString();
      const dateKey = formatTimestamp(ts); // Format the date to "May 5"
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(msg);
    }

    return grouped;
  };
  const renderAttachment = (msg) => {
    const isEditing =
      msg.__customFile instanceof File || msg.__customFile instanceof Blob;

    // Support local files for edit preview
    const localFile = isEditing ? msg.__customFile : null;
    const fileUrl = localFile ? URL.createObjectURL(localFile) : null;

    // Regular base64 content for saved messages
    const base64Url = msg.contentAttachment
      ? `data:${msg.attachmentType};base64,${msg.contentAttachment}`
      : null;

    const fileType = localFile?.type || msg.attachmentType || "";

    const url = fileUrl || base64Url;
    if (!url) return null;

    // Audio preview
    if (fileType.startsWith("audio")) {
      return (
        <Card
          size="small"
          style={{ width: "100%", marginTop: 8 }}
          bodyStyle={{ padding: 12 }}
        >
          <audio controls src={url} style={{ width: "100%" }} />
        </Card>
      );
    }

    // Image preview
    if (fileType.startsWith("image")) {
      return (
        <Card
          size="small"
          hoverable
          cover={
            <img
              alt={msg.content || "Image"}
              src={url}
              style={{ objectFit: "cover", maxHeight: 200 }}
            />
          }
          style={{ width: 200, marginTop: 8 }}
        >
          <Card.Meta title={msg.content || (localFile?.name ?? "Image")} />
        </Card>
      );
    }

    // Other file types (pdf, doc, etc.)
    return (
      <Card
        size="small"
        style={{ width: 200, marginTop: 8 }}
        bodyStyle={{ padding: 12 }}
        actions={[
          <Button
            type="link"
            icon={<DownloadOutlined />}
            href={url}
            download={msg.content || localFile?.name}
          >
            Download
          </Button>,
        ]}
      >
        <Card.Meta
          avatar={<FileOutlined style={{ fontSize: 24, color: "#1890ff" }} />}
          title={msg.content || (localFile?.name ?? "Attachment")}
        />
      </Card>
    );
  };

  const isSentByUser = (msg) => {
    const id = msg.senderId || msg.sender?.id || msg.sender;
    return id === senderId;
  };

  const getInitials = (senderName) => {
    if (senderName === "You") return "Y";

    if (!senderName) return "";

    const [firstPart] = senderName.split("@");
    const initials = firstPart
      .split(".")
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
    return initials;
  };

  return selectedChat ? (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-100 d-flex flex-column bg-light rounded shadow"
    >
      <div className="flex-grow-1 overflow-auto p-3">
        {Object.entries(getGroupedMessages()).map(([dateKey, msgs]) => (
          <div key={dateKey}>
            <div className="text-center text-muted small mb-2 fw-bold text-uppercase">
              {dateKey}
            </div>
            {msgs.map((msg) => (
              <motion.div
                key={msg.messageId}
                id={`msg-${msg.messageId}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`d-flex mb-3 ${
                  isSentByUser(msg)
                    ? "justify-content-end"
                    : "justify-content-start"
                }`}
              >
                {/* Sender's Avatar and Details */}
                {!isSentByUser(msg) && (
                  <div className="me-2 text-center">
                    <Tooltip title={msg.senderName}>
                      <Avatar
                        style={{
                          backgroundColor: "#1890ff",
                          width: 40,
                          height: 40,
                          fontSize: 18,
                          lineHeight: "40px",
                          textAlign: "center",
                        }}
                      >
                        {getInitials(msg.senderName)}
                      </Avatar>
                    </Tooltip>
                    <div
                      className="small text-muted mt-1"
                      style={{
                        fontSize: "12px",
                        color: "#888",
                      }}
                    >
                      {new Date(
                        msg.sentAt || msg.timestamp || msg.createdAt
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                )}

                {/* Message Bubble */}
                <div
                  className={`position-relative d-inline-block ${
                    isSentByUser(msg)
                      ? "bg-primary text-white"
                      : "bg-white text-dark"
                  } p-3 rounded-3 shadow-sm`}
                  style={{
                    maxWidth: "75%",
                    borderRadius: "18px",
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    padding: "12px 16px",
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for realism
                  }}
                >
                  {/* Speech Bubble Tail */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                      ...(isSentByUser(msg)
                        ? {
                            right: "-10px",
                            borderWidth: "10px 0 10px 10px",
                            borderColor:
                              "transparent transparent transparent #1677ff",
                          }
                        : {
                            left: "-10px",
                            borderWidth: "10px 10px 10px 0",
                            borderColor:
                              "transparent #ffffff transparent transparent",
                          }),
                    }}
                  />
                  {/* ── Reply Preview ── */}
                  {msg.replied_To_Id && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        background: isSentByUser(msg)
                          ? "rgba(255,255,255,0.15)"
                          : "#fafafa",
                        borderLeft: `4px solid ${
                          isSentByUser(msg) ? "#ffffff" : "#1890ff"
                        }`,
                        padding: "8px 12px",
                        marginBottom: 8,
                        borderRadius: 8,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                      }}
                      onClick={() => {
                        // 2) make it clickable
                        const target = document.getElementById(
                          `msg-${msg.replied_To_Id}`
                        );
                        if (target) {
                          target.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }}
                      className="reply-preview-clickable"
                    >
                      <Text
                        strong
                        style={{
                          color: isSentByUser(msg) ? "#fff" : "#1890ff",
                          marginBottom: 4,
                        }}
                      >
                        {msg.repliedToSenderName}
                      </Text>
                      <Text
                        ellipsis={{ rows: 1, expandable: false }}
                        style={{
                          margin: 0,
                          fontStyle: "italic",
                          color: isSentByUser(msg) ? "#eee" : "#595959",
                        }}
                      >
                        {msg.repliedToContent}
                      </Text>
                    </div>
                  )}
                  {/* Message Content */}
                  {msg.content &&
                    (editingMessageId === msg.id ? (
                      <Tooltip title="Press Enter to save">
                        <Input
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onPressEnter={handleSaveEdit}
                          onBlur={handleSaveEdit}
                          autoFocus
                          bordered={false}
                        />
                      </Tooltip>
                    ) : (
                      <div>{msg.content}</div>
                    ))}{" "}
                  {renderAttachment(msg)}
                  {msg.updatedAt && (
                    <div className="text-end mt-1">
                      <Text
                        italic
                        className="small"
                        style={{ fontSize: "10px", color: "#ffffff" }}
                      >
                        Edited
                      </Text>
                    </div>
                  )}
                </div>

                {/* "You" Badge for Sent Messages */}
                {
                  // show menu for sender (edit/delete),
                  // or for anyone with reply access,
                  // or for group admin
                  (isSentByUser(msg) ||
                    msg.isReplyAllowed ||
                    (selectedChat.type === "group" &&
                      storedUser.userId === selectedChat.groupAdminUserID)) && (
                    <div className="ms-2 text-center">
                      <Dropdown
                        overlay={
                          <Menu>
                            {/* only show Reply when user is receiver & replyAllowed */}
                            {msg.isReplyAllowed && (
                              <Menu.Item
                                key="reply"
                                icon={<CommentOutlined />}
                                onClick={() => handleReplyClick(msg)}
                              >
                                Reply
                              </Menu.Item>
                            )}
                            {/* Edit/Delete only for sender or admin */}
                            {(isSentByUser(msg) ||
                              (selectedChat.type === "group" &&
                                storedUser.userId ===
                                  selectedChat.groupAdminUserID)) && (
                              <>
                                <Menu.Item
                                  key="edit"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditClick(msg)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  key="delete"
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    handleDeleteClick(msg.messageId)
                                  }
                                >
                                  Delete
                                </Menu.Item>
                              </>
                            )}
                          </Menu>
                        }
                        trigger={["click"]}
                      >
                        <EllipsisOutlined
                          style={{ marginLeft: 8, cursor: "pointer" }}
                        />
                      </Dropdown>
                      <Tooltip title="You">
                        <Avatar
                          style={{
                            backgroundColor: "#52c41a", // Green for sent messages
                            width: 45,
                            height: 45,
                            fontSize: 18,
                            lineHeight: "45px",
                            textAlign: "center",
                          }}
                        >
                          {getInitials("You")}
                        </Avatar>
                      </Tooltip>
                      <div
                        className="small text-muted mt-1"
                        style={{
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        {new Date(
                          msg.sentAt || msg.timestamp || msg.createdAt
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  )
                }
              </motion.div>
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input and Attachments */}
      <div className="d-flex align-items-center gap-2 p-3 border-top bg-white rounded-bottom shadow-sm">
        <FileAttachmentComponent
          onSend={(file) => {
            setPendingAttachments((prev) => [...prev, { file, type: "file" }]);
          }}
        />
        <AudioRecorderComponent
          onSend={(file) => {
            setPendingAttachments((prev) => [...prev, { file, type: "audio" }]);
          }}
        />
        <EmojiPickerComponent
          onEmojiSelect={(emoji) => setInput((prev) => prev + emoji)}
        />
        {pendingAttachments.length > 0 && (
          <div className="d-flex gap-2 mb-2 flex-wrap">
            {pendingAttachments.map(({ file, type }, idx) => (
              <Card
                key={idx}
                size="small"
                style={{ width: 120 }}
                bodyStyle={{ padding: "8px" }}
                actions={[
                  <CloseOutlined
                    onClick={() =>
                      setPendingAttachments((a) =>
                        a.filter((_, i) => i !== idx)
                      )
                    }
                  />,
                ]}
              >
                {type === "audio" ? (
                  <audio
                    controls
                    src={
                      file instanceof Blob
                        ? URL.createObjectURL(file)
                        : file /* maybe it’s already a data URL */
                    }
                    style={{ width: "100%" }}
                  />
                ) : (
                  <>
                    <FileOutlined style={{ fontSize: 32 }} />
                    <Text ellipsis style={{ width: "100%" }}>
                      {file.name}
                    </Text>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
        {/* ── Replying Bar ── */}
        {replyTarget && (
          <div
            className="d-flex align-items-center mb-2 p-2 rounded"
            style={{
              background: "#f0f0f0",
              borderLeft: "4px solid #1890ff",
              maxWidth: "100%",
            }}
          >
            <div style={{ flex: 1, overflow: "hidden" }}>
              <Text strong ellipsis style={{ display: "block" }}>
                Replying to {replyTarget.senderName}
              </Text>
              <Text ellipsis style={{ display: "block", color: "#595959" }}>
                {replyTarget.content || replyTarget.repliedToContent}
              </Text>
            </div>
            <CloseOutlined
              onClick={() => setReplyToId(null)}
              style={{ fontSize: 16, cursor: "pointer", marginLeft: 8 }}
            />
          </div>
        )}
        {editingMessageId && (
          <div
            className="mb-2 p-2 rounded shadow-sm"
            style={{ background: "#fff7e6", borderLeft: "4px solid #fa8c16" }}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-yellow-700">
                Editing Message
              </span>
              <CloseOutlined
                onClick={() => {
                  setEditingMessageId(null);
                  setEditText("");
                  setEditAttachment(null);
                }}
                className="cursor-pointer text-gray-500 hover:text-red-500"
              />
            </div>

            <Input.TextArea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="Edit message..."
            />

            {editAttachment && (
              <div className="mt-2">
                {renderAttachment({ __customFile: editAttachment })}
              </div>
            )}

            <Upload.Dragger
              accept="*/*"
              showUploadList={false}
              beforeUpload={(file) => {
                setEditAttachment(file);
                return false;
              }}
              style={{
                marginTop: 10,
                padding: 8,
                background: "#fafafa",
                borderRadius: 6,
              }}
            >
              <p>Drag & drop to update attachment, or click to select</p>
            </Upload.Dragger>

            <div className="mt-2">
              <AudioRecorderComponent
                onSend={(blob) => {
                  setEditAttachment(blob);
                }}
              />
            </div>

            <div className="mt-2 text-right">
              <Button type="primary" onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </div>
          </div>
        )}

        <Input
          placeholder="Type a message"
          value={editingMessageId ? editText : input}
          onChange={(e) =>
            editingMessageId
              ? setEditText(e.target.value)
              : setInput(e.target.value)
          }
          onPressEnter={editingMessageId ? handleSaveEdit : handleSend}
          disabled={isRecording}
        />
        <Button
          icon={<SendOutlined />}
          onClick={editingMessageId ? handleSaveEdit : handleSend}
          type="primary"
          disabled={
            (editingMessageId ? editText.trim() === "" : input.trim() === "") &&
            pendingAttachments.length === 0
          }
        />
      </div>
    </motion.div>
  ) : null;
}
